// Les graphiques du tableau de bord, dessinés en SVG.
//
// Pourquoi pas une bibliothèque : ces trois formes-là (colonnes, barres
// horizontales, entonnoir) tiennent en quelques dizaines de lignes, et le CRM
// s'ouvre au bord d'un chantier, en 4G, sur un téléphone — 100 Ko de plus dans
// le bundle se paient à chaque ouverture.
//
// Les règles de lecture appliquées ici, une fois pour toutes :
//   - les marques portent la couleur, jamais le texte (illisible en clair) ;
//   - deux marques qui se touchent sont séparées par un vide, pas par un trait ;
//   - on n'étiquette pas chaque valeur : l'axe, la légende et le survol suffisent ;
//   - un seul axe de valeurs, jamais deux échelles superposées.

import { useEffect, useState } from 'react';

import { formatEuro } from '@/modules/catalogue/api';

/**
 * Vrai sur un écran de téléphone. Douze colonnes y tiendraient, mais leurs
 * libellés seraient réduits à des traits : on préfère montrer six mois lisibles
 * que douze illisibles.
 */
function useEcranEtroit(): boolean {
  const [etroit, setEtroit] = useState(
    () => typeof window !== 'undefined' && !window.matchMedia('(min-width: 640px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 640px)');
    const suivre = () => {
      setEtroit(!mq.matches);
    };
    suivre();
    mq.addEventListener('change', suivre);
    return () => {
      mq.removeEventListener('change', suivre);
    };
  }, []);
  return etroit;
}

/**
 * Une colonne : coins arrondis du côté des données, carrés sur la ligne de base
 * — la barre doit sembler poser sur l'axe, pas flotter au-dessus.
 */
function colonne(x: number, y: number, l: number, h: number, r = 3): string {
  const rayon = Math.min(r, l / 2, Math.max(0, h));
  const bas = y + Math.max(0, h);
  return `M ${String(x)} ${String(bas)} L ${String(x)} ${String(y + rayon)} Q ${String(x)} ${String(y)} ${String(x + rayon)} ${String(y)} L ${String(x + l - rayon)} ${String(y)} Q ${String(x + l)} ${String(y)} ${String(x + l)} ${String(y + rayon)} L ${String(x + l)} ${String(bas)} Z`;
}

/** Arrondit le haut de l'axe à un nombre rond, pour des graduations lisibles. */
function plafond(valeurMax: number): number {
  if (valeurMax <= 0) return 1;
  const puissance = 10 ** Math.floor(Math.log10(valeurMax));
  for (const pas of [1, 2, 2.5, 5, 10]) {
    const candidat = pas * puissance;
    if (candidat >= valeurMax) return candidat;
  }
  return 10 * puissance;
}

/** Les euros de l'axe, courts : 12 k€, 1,2 M€. */
function euroCourt(v: number): string {
  if (Math.abs(v) >= 1_000_000)
    return `${(v / 1_000_000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} M€`;
  if (Math.abs(v) >= 1000) return `${Math.round(v / 1000).toLocaleString('fr-FR')} k€`;
  return `${Math.round(v).toLocaleString('fr-FR')} €`;
}

export interface SerieMois {
  label: string;
  factureHt: number;
  signeHt: number;
}

/**
 * Facturé contre signé, mois par mois. Deux séries, donc une légende — et les
 * deux colonnes d'un même mois se touchent presque : c'est le vide de 2 px
 * entre elles qui les sépare, pas un contour.
 */
export function GraphiqueMois({ points }: { points: SerieMois[] }) {
  const etroit = useEcranEtroit();
  // Sur téléphone, moins de mois et un cadre plus étroit : le tracé est agrandi
  // d'autant, donc les libellés et les graduations restent lisibles.
  const visibles = etroit ? points.slice(-6) : points;
  const L = etroit ? 320 : 560;
  const H = etroit ? 200 : 220;
  const margeG = 52;
  const margeB = 28;
  const margeH = 12;
  const max = plafond(Math.max(1, ...visibles.flatMap((p) => [p.factureHt, p.signeHt])));
  const largeurTrace = L - margeG - 8;
  const hauteurTrace = H - margeH - margeB;
  const pasBande = largeurTrace / Math.max(1, visibles.length);
  // La barre reste fine : la bande garde de l'air, même sur douze mois.
  const largeurBarre = Math.min(12, (pasBande - 4) / 2);
  const y = (v: number) => margeH + hauteurTrace * (1 - v / max);

  return (
    <figure className="m-0">
      {/* La hauteur suit la largeur (le viewBox donne le rapport) : à hauteur
          figée, le tracé se retrouverait mis en boîte au milieu du cadre. */}
      <svg
        viewBox={`0 0 ${String(L)} ${String(H)}`}
        className="h-auto w-full"
        role="img"
        aria-label="Chiffre d'affaires facturé et devis signés, mois par mois"
      >
        {[0, 0.25, 0.5, 0.75, 1].map((f) => (
          <g key={f}>
            <line
              x1={margeG}
              x2={L - 8}
              y1={y(max * f)}
              y2={y(max * f)}
              className="stroke-border"
              strokeWidth={1}
            />
            <text
              x={margeG - 8}
              y={y(max * f) + 4}
              textAnchor="end"
              className="fill-muted-foreground text-[10px] tabular-nums"
            >
              {euroCourt(max * f)}
            </text>
          </g>
        ))}
        {visibles.map((p, i) => {
          const x = margeG + i * pasBande + (pasBande - 2 * largeurBarre - 2) / 2;
          return (
            <g key={p.label}>
              <path
                d={colonne(
                  x,
                  y(Math.max(0, p.factureHt)),
                  largeurBarre,
                  hauteurTrace - (y(Math.max(0, p.factureHt)) - margeH),
                )}
                className="fill-money-in"
              >
                <title>{`${p.label} — facturé ${formatEuro(p.factureHt)} HT`}</title>
              </path>
              <path
                d={colonne(
                  x + largeurBarre + 2,
                  y(Math.max(0, p.signeHt)),
                  largeurBarre,
                  hauteurTrace - (y(Math.max(0, p.signeHt)) - margeH),
                )}
                className="fill-prio-low"
              >
                <title>{`${p.label} — signé ${formatEuro(p.signeHt)} HT`}</title>
              </path>
              <text
                x={margeG + i * pasBande + pasBande / 2}
                y={H - 8}
                textAnchor="middle"
                className="fill-muted-foreground text-[10px]"
              >
                {p.label}
              </text>
            </g>
          );
        })}
      </svg>
      <figcaption className="text-muted-foreground mt-2 flex flex-wrap gap-4 text-xs">
        <span className="flex items-center gap-1.5">
          <span className="bg-money-in h-2.5 w-2.5 rounded-sm" /> Facturé HT
        </span>
        <span className="flex items-center gap-1.5">
          <span className="bg-prio-low h-2.5 w-2.5 rounded-sm" /> Devis signés HT
        </span>
        <span>{visibles.length} derniers mois</span>
      </figcaption>
    </figure>
  );
}

export interface BarreImpaye {
  label: string;
  montant: number;
  nombre: number;
}

/**
 * La balance âgée : une seule série, donc pas de légende — le titre dit ce qui
 * est tracé. La teinte suit l'ancienneté (plus c'est vieux, plus c'est sombre),
 * et chaque barre porte son montant en clair : c'est le chiffre qu'on vient
 * chercher.
 */
export function GraphiqueImpayes({ tranches }: { tranches: BarreImpaye[] }) {
  const max = Math.max(1, ...tranches.map((t) => t.montant));
  // Une échelle d'ancienneté, pas des catégories : une seule teinte, du clair au foncé.
  const teintes = ['bg-red-200', 'bg-red-300', 'bg-red-500', 'bg-red-700'];
  const teintesSombre = [
    'dark:bg-red-900',
    'dark:bg-red-800',
    'dark:bg-red-600',
    'dark:bg-red-500',
  ];

  return (
    <div className="space-y-3">
      {tranches.map((t, i) => (
        <div key={t.label} className="flex items-center gap-3">
          <span className="text-muted-foreground w-20 shrink-0 text-xs">{t.label}</span>
          <div className="bg-muted/50 h-5 flex-1 overflow-hidden rounded-sm">
            <div
              className={`h-full rounded-sm ${teintes[i] ?? ''} ${teintesSombre[i] ?? ''}`}
              style={{ width: `${String(Math.round((t.montant / max) * 100))}%` }}
              title={`${t.label} : ${formatEuro(t.montant)} sur ${String(t.nombre)} facture(s)`}
            />
          </div>
          <span className="w-28 shrink-0 text-right text-sm font-semibold tabular-nums">
            {t.montant > 0 ? formatEuro(t.montant) : '—'}
          </span>
        </div>
      ))}
    </div>
  );
}

export interface EtapeEntonnoirVue {
  label: string;
  nombre: number;
  montantHt: number;
}

/**
 * L'entonnoir devis : trois étapes chaînées, chacune ramenée à la première.
 * C'est une part-du-tout ordonnée — les largeurs décroissent, et le taux de
 * passage se lit entre deux étapes.
 */
export function Entonnoir({ etapes }: { etapes: EtapeEntonnoirVue[] }) {
  const base = Math.max(1, etapes[0]?.nombre ?? 0);
  return (
    <div className="space-y-3">
      {etapes.map((e, i) => {
        const part = e.nombre / base;
        return (
          <div key={e.label}>
            <div className="flex items-baseline justify-between text-sm">
              <span className="font-medium">{e.label}</span>
              <span className="text-muted-foreground tabular-nums">
                {e.nombre} · {formatEuro(e.montantHt)} HT
              </span>
            </div>
            <div className="bg-muted/50 mt-1 h-5 overflow-hidden rounded-sm">
              <div
                className="bg-prio-low h-full rounded-sm"
                style={{
                  width: `${String(Math.round(part * 100))}%`,
                  // Les étapes se lisent comme une progression : la même teinte
                  // qui pâlit, jamais trois couleurs qui feraient croire à trois sujets.
                  opacity: 1 - i * 0.25,
                }}
                title={`${e.label} : ${String(e.nombre)} devis, ${formatEuro(e.montantHt)} HT`}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
